import { useEffect, useState } from 'react';
import { ShieldCheck, Wallet } from 'lucide-react';
import { getAuthMe } from '@/api/auth';
import { postWalletCreate, walletError, type WalletCreateBody } from '@/api/parent';
import { input } from './ui';

const empty: WalletCreateBody = { bvn: '', first_name: '', last_name: '', date_of_birth: '', phone_number: '', email: '', address: '' };

function L({ t, opt, children }: { t: string; opt?: boolean; children: React.ReactNode }) {
  return <label className="block"><span className="text-sm font-semibold text-slate-700">{t}{opt && <span className="font-normal text-slate-400"> (optional)</span>}</span><div className="mt-1">{children}</div></label>;
}

/** Open the parent's wallet: BVN goes to XpressWallet only and is never stored. Name and phone are filled in from the account. */
export function OnboardingForm({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState<WalletCreateBody>(empty);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getAuthMe().then((r: any) => {
      const u = r?.data?.data?.user ?? {};
      setF((p) => ({ ...p, first_name: p.first_name || u.first_name || '', last_name: p.last_name || u.last_name || '', phone_number: p.phone_number || u.phone || '', email: p.email || (String(u.email ?? '').includes('@') ? u.email : '') }));
    }).catch(() => undefined);
  }, []);

  const set = (k: keyof WalletCreateBody) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));
  const needed: (keyof WalletCreateBody)[] = ['bvn', 'first_name', 'last_name', 'date_of_birth', 'phone_number', 'address'];
  const valid = /^\d{11}$/.test(f.bvn) && needed.every((k) => String(f[k]).trim() !== '') && (!f.email || /^\S+@\S+\.\S+$/.test(f.email));

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await postWalletCreate({ ...f, email: f.email || (undefined as unknown as string) });
      setF(empty); // never keep the BVN around
      onDone();
    } catch (e) {
      setError(walletError(e).message);
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#12406b] text-white p-5">
        <Wallet className="w-7 h-7 text-green-300" />
        <h2 className="text-xl font-extrabold mt-2">Open your wallet</h2>
        <p className="text-sm text-white/75 mt-1">Get your own account number. Top it up from any bank, then pay school fees in one tap and send money to any bank.</p>
      </div>
      <div className="p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <L t="BVN"><input className={`${input} font-mono tracking-wider`} placeholder="11 digits" inputMode="numeric" maxLength={11} autoComplete="off" value={f.bvn} onChange={(e) => setF((p) => ({ ...p, bvn: e.target.value.replace(/\D/g, '') }))} /></L>
          <L t="Date of birth"><input className={input} type="date" value={f.date_of_birth} onChange={set('date_of_birth')} /></L>
          <L t="First name (as on BVN)"><input className={input} value={f.first_name} onChange={set('first_name')} /></L>
          <L t="Last name (as on BVN)"><input className={input} value={f.last_name} onChange={set('last_name')} /></L>
          <L t="Phone number"><input className={input} inputMode="tel" value={f.phone_number} onChange={set('phone_number')} /></L>
          <L t="Email" opt><input className={input} type="email" autoComplete="email" value={f.email} onChange={set('email')} /></L>
        </div>
        <L t="Home address"><input className={input} value={f.address} onChange={set('address')} /></L>
        <p className="text-xs text-slate-500 flex gap-1.5"><ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />Your BVN is sent securely to our licensed payment partner to open the wallet. This app never stores it.</p>
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        <button disabled={!valid || busy} onClick={submit} className="w-full py-3 rounded-xl bg-[#0A2540] text-white font-bold text-sm disabled:opacity-40">{busy ? 'Opening your wallet…' : 'Open wallet'}</button>
      </div>
    </section>
  );
}
